import { useState, useEffect } from "react";
import { toast } from "sonner";
import { X, FolderSimple, ArrowsClockwise } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { getProjectFolders, moveRecording } from "../../api/recordings";

const ROOT_VALUE = "__root__";

/**
 * MoveRecordingModal
 * Props:
 *   recording   — { id, project_id, project_name, folder_id }
 *   onClose()
 *   onMoved(updatedRecording)
 */
export default function MoveRecordingModal({ recording, onClose, onMoved }) {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onedriveLinked, setOnedriveLinked] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selected, setSelected] = useState(recording.folder_id || ROOT_VALUE);
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    getProjectFolders(recording.project_id)
      .then((res) => {
        setFolders(res.folders || []);
        setOnedriveLinked(res.onedrive_linked !== false);
      })
      .catch(() => {
        setLoadError(true);
        toast.error("Could not load folders for this project.");
      })
      .finally(() => setLoading(false));
  }, [recording.project_id]);

  const handleMove = async () => {
    setMoving(true);
    try {
      const folderId = selected === ROOT_VALUE ? null : selected;
      const updated = await moveRecording(recording.id, folderId);
      toast.success("Recording uploaded to OneDrive.");
      onMoved(updated);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not upload this recording to OneDrive.");
    } finally {
      setMoving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="move-recording-modal-backdrop"
    >
      <div className="bg-white border border-slate-200 w-full max-w-sm shadow-2xl rounded-lg overflow-hidden" data-testid="move-recording-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <p className="text-sm font-semibold text-slate-800">Move to Folder</p>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 transition-colors" data-testid="move-recording-modal-close">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Destination Folder</label>
            <Select value={selected} onValueChange={setSelected} disabled={loading}>
              <SelectTrigger data-testid="move-recording-folder-select">
                <SelectValue placeholder={loading ? "Loading folders..." : "Select a folder"} />
              </SelectTrigger>
              <SelectContent className="z-[80]">
                <SelectItem value={ROOT_VALUE} data-testid="move-recording-folder-option-root">
                  <span className="flex items-center gap-2">
                    <FolderSimple size={13} className="text-slate-400" /> Project Root Folder
                  </span>
                </SelectItem>
                {folders.map((f) => (
                  <SelectItem key={f.id} value={f.id} data-testid={`move-recording-folder-option-${f.id}`}>
                    <span className="flex items-center gap-2">
                      <FolderSimple size={13} className="text-[#0078D4]" /> {f.path}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!loading && !loadError && !onedriveLinked && (
              <p className="text-xs text-slate-400">This project doesn&apos;t have an OneDrive folder linked yet.</p>
            )}
            {!loading && !loadError && onedriveLinked && folders.length === 0 && (
              <p className="text-xs text-slate-400">This project has no subfolders yet.</p>
            )}
            {!loading && loadError && (
              <p className="text-xs text-red-500">Could not load folders — close and try again.</p>
            )}
            <p className="text-xs text-slate-400">The recording&apos;s audio will be uploaded as a file named with today&apos;s date and time (so multiple recordings never overwrite each other).</p>
          </div>

          <Button
            onClick={handleMove}
            disabled={loading || moving}
            className="w-full bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="move-recording-confirm-btn"
          >
            {moving ? <ArrowsClockwise size={14} className="mr-2 animate-spin" /> : null}
            {moving ? "Uploading..." : "Move File"}
          </Button>
        </div>
      </div>
    </div>
  );
}
